// ═══════════════════════════════════════════════════════════════════════════
// DOMAINE : payload de CRÉATION d'une action depuis l'éditeur
// ═══════════════════════════════════════════════════════════════════════════
//
// `renderActionEditor` rend, via `onSaved`, l'action telle que saisie. Chaque
// page qui héberge l'éditeur en mode création (liste #actions, page #action/0,
// post-its) reconstruisait ensuite SON payload `POST /actions`, champ par
// champ — trois copies qui divergeaient à chaque nouveau champ (le flag
// « confidentiel » avait déjà été oublié une fois, juin 2026 ; livrable attendu
// et lien outil auraient suivi).
//
// Une seule fonction pure ici, appelée par les trois. Ajouter un champ à
// l'éditeur = l'ajouter ICI, et il est transmis partout.

/** Action telle que rendue par l'éditeur à `onSaved` (champs optionnels). */
export interface EditorSavedAction {
  text?: string;
  deadline?: string | null;
  deadline_revised?: string | null;
  status_id?: number | null;
  progress?: number;
  priority?: number | null;
  expected_deliverable?: string | null;
  external_link?: string | null;
  approver_id?: number | null;
  performers?: unknown[];
  consulted?: unknown[];
  informed?: unknown[];
  is_confidential?: boolean;
  owner_id?: number | null;
  project_ids?: number[];
  committee_ids?: number[];
  structure_ids?: number[];
}

/**
 * Payload `POST /api/v1/actions` correspondant à ce que l'éditeur a saisi.
 *
 * `structureIdsFallback` : organisation issue d'un pré-remplissage (CTA
 * « Dupliquer », contrôle de réunion) — l'éditeur ne l'expose pas en création,
 * elle serait perdue sans ce repli.
 */
export function buildCreateActionPayload(
  d: EditorSavedAction,
  opts: { structureIdsFallback?: number[] | undefined } = {},
): Record<string, unknown> {
  return {
    text: d.text,
    deadline: d.deadline,
    deadline_revised: d.deadline_revised,
    status_id: d.status_id,
    progress: d.progress || 0,
    approver_id: d.approver_id,
    performers: d.performers,
    consulted: d.consulted,
    informed: d.informed,
    priority: d.priority,
    expected_deliverable: d.expected_deliverable,
    external_link: d.external_link,
    // Confidentialité : à NE PAS oublier à la création (sinon le flag coché
    // dans l'éditeur est perdu — bug juin 2026).
    is_confidential: d.is_confidential ?? false,
    // Owner : l'éditeur expose le picker dès la création, mais les trois
    // payloads d'origine l'oubliaient — l'owner désigné était perdu.
    owner_id: d.owner_id ?? null,
    project_ids: d.project_ids || [],
    committee_ids: d.committee_ids || [],
    structure_ids: d.structure_ids || opts.structureIdsFallback || [],
  };
}
